import { useCallback, useMemo, useState } from 'react';
import type { AggregateResponse, Modality, SessionResult } from '../types';
import { submitResult, fetchAggregate } from '../lib/api';
import { exportSession } from '../lib/export';
import { useLabStore } from '../store/useLabStore';
import { formatThreshold } from '../lib/color';
import { TurnstileWidget } from './TurnstileWidget';

interface Props {
  aggregate: AggregateResponse | null;
  selected: Modality;
}

export function ResultsPanel({ aggregate, selected }: Props) {
  const { lastResult, uploadState, uploadError, setUploadState, setAggregate } = useLabStore();
  const [token, setToken] = useState<string | null>(null);
  const percentile = useMemo(() => lastResult ? percentileFor(lastResult, aggregate) : null, [lastResult, aggregate]);

  const upload = useCallback(async () => {
    if (!lastResult || !token) return;
    setUploadState('uploading');
    const outcome = await submitResult(lastResult, token);
    if (!outcome.ok) {
      setUploadState('failed', outcome.error);
      return;
    }
    setUploadState('uploaded');
    const fresh = await fetchAggregate();
    setAggregate(fresh);
  }, [lastResult, token, setUploadState, setAggregate]);

  if (!lastResult) {
    return (
      <section className="lab-panel results-panel">
        <p className="eyebrow">personal result</p>
        <h2>No edge yet</h2>
        <p>Run a threshold task; your local result will appear here before anything is uploaded.</p>
      </section>
    );
  }

  return (
    <section className="lab-panel results-panel">
      <div className="panel-header">
        <div>
          <p className="eyebrow">personal result</p>
          <h2>{formatThreshold(lastResult.modality, lastResult.threshold)}</h2>
        </div>
        <button className="ghost-button" onClick={() => exportSession(lastResult)}>Export JSON</button>
      </div>
      <p className="result-copy">
        68% credible interval: {formatThreshold(lastResult.modality, lastResult.thresholdLow)} → {formatThreshold(lastResult.modality, lastResult.thresholdHigh)}.
        {percentile !== null ? ` You are around the ${ordinal(percentile)} percentile among uploaded ${lastResult.modality} runs.` : ''}
      </p>

      <div className="consent-line" role="note">
        I consent to upload anonymous device-keyed threshold summaries and trial timings to a public aggregate commons; no account, name, or raw media.
      </div>

      <TurnstileWidget onToken={setToken} />

      <button className="primary-button wide" onClick={upload} disabled={!token || uploadState === 'uploading' || uploadState === 'uploaded'}>
        {uploadState === 'uploading' ? 'Adding to commons…' : uploadState === 'uploaded' ? 'Added to commons' : 'Add my edge to the commons'}
      </button>
      {uploadState === 'failed' && uploadError ? <p className="error-note">{uploadError}</p> : null}
      <p className="microcopy">
        Your private device secret stays in this browser. The server receives only a SHA-256 device key, threshold summary, compact trial log, and coarse device capability buckets.
      </p>
    </section>
  );
}

function percentileFor(result: SessionResult, aggregate: AggregateResponse | null): number | null {
  const bins = aggregate?.modalities[result.modality]?.bins ?? [];
  const total = bins.reduce((sum, b) => sum + b.count, 0);
  if (!total) return null;
  let below = 0;
  for (const bin of bins) {
    if (result.threshold >= bin.hi) below += bin.count;
    else if (result.threshold > bin.lo) {
      const span = bin.hi - bin.lo || 1;
      below += bin.count * ((result.threshold - bin.lo) / span);
      break;
    } else break;
  }
  return Math.round((below / total) * 100);
}

function ordinal(n: number): string {
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  return `${n}${['th', 'st', 'nd', 'rd'][Math.min(n % 10, 4)] ?? 'th'}`;
}
